import React, { useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTheme } from '../../../contexts/ThemeContext'
import { useMobile } from '../../../contexts/MobileContext';
import { getAxisChartGrid, getChartLegendLayout, getChartSemanticColors, getChartTheme, getChartTooltipMarker, getChartTooltipStyle, getChartTypography } from '../charting/chartTheme';
import useChartAutoResize from '../charting/useChartAutoResize';

function computeDateLabels(lastDay, count) {
  const labels = [];
  const oneDay = 24 * 60 * 60 * 1000;
  let [day, month, year] = lastDay;
  let end = new Date(year, month, day);

  let start = new Date(end);
  start.setDate(start.getDate() - count + 1);

  let current = new Date(start);

  while (current <= end) {
    const next = new Date(current.getTime() + 4 * oneDay);
    const rangeEnd = next < end ? next : end;

    const startLabel = current.toLocaleDateString('en-US', { month: 'short',
                                                             day: '2-digit' });
    const endLabel = rangeEnd.toLocaleDateString('en-US', { month: 'short',
                                                            day: '2-digit' });

    labels.push(`${startLabel} - ${endLabel}`);
    current = new Date(rangeEnd.getTime() + oneDay);
  }

  return labels;
}

function findDateRange(dateString, ranges) {
  const parsedDate = new Date(dateString);
  let day = parsedDate.getDate();
  let month = parsedDate.getMonth();
  let year = parsedDate.getFullYear();
  let targetDate = new Date(year, month, day);

  for (const range of ranges) {
    const [startStr, endStr] = range.split(' - ');
    const startDate = new Date(`${startStr} ${targetDate.getFullYear()}`);
    const endDate = new Date(`${endStr} ${targetDate.getFullYear()}`);

    if (startDate > endDate) {
      endDate.setFullYear(endDate.getFullYear() + 1);
    }

    if (targetDate >= startDate && targetDate <= endDate) {
      return range;
    }
  }
  return null;
}

const getOption = (specs, theme, isMobile, showLegend = true) => {
  const colors = getChartSemanticColors(theme);
  const typography = getChartTypography(isMobile);
  const {values, lastDay} = specs;

  const botClicks = values.bot;
  const humanClicks = values.human;
  const humanLabel = 'Human Traffic';
  const botLabel = 'Bot Traffic';

  let bins = computeDateLabels(lastDay, values.bot.length);

  let human = {};
  let bot = {};

  for (let bin of bins) {
    human[bin] = 0;
    bot[bin] = 0;
  }

  let [day, month, year] = lastDay;
  let date = new Date(year, month, day);
  date.setDate(date.getDate() - botClicks.length + 1);

  for (let i = 0; i < botClicks.length; i++) {
    human[findDateRange(date, bins)] += humanClicks[i];
    bot[findDateRange(date, bins)] += botClicks[i];
    date.setDate(date.getDate() + 1);
  }

  let humanData = [];
  let botData = [];

  for (let bin of bins) {
    humanData.push(human[bin]);
    botData.push(bot[bin]);
  }

  return {
    title: {
      text: '',
    },
    tooltip: getChartTooltipStyle(theme, typography, {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: theme.primaryA05,
        },
      },
      formatter: function (params) {
        let tooltipContent = '';

        tooltipContent += `${params[0].name} <br/>`;

        params.forEach((item) => {
          const name = item.seriesName.startsWith('Human') ? 'Human' : 'Bot';
          const color = item.seriesName.startsWith('Human') ? colors.human : colors.bot;

          const value = item.value;
          tooltipContent += `
          ${getChartTooltipMarker(color)}
          ${name}: ${value}<br/>
        `;
        });

        return tooltipContent;
      },
    }),
    ...(showLegend ? {
      legend: {
        ...getChartLegendLayout(isMobile, theme),
        data: [
          {icon: 'square', name: humanLabel},
          {icon: 'square', name: botLabel}
        ],
        textStyle: {
          color: theme.textHigh,
          fontWeight: typography.strongWeight,
          fontSize: typography.legendSize,
        },
      },
    } : {}),
    grid: getAxisChartGrid(isMobile, { hasLegend: showLegend }),
    xAxis: {
      type: 'category',
      data: bins,
      boundaryGap: isMobile ? ['8%', '8%'] : ['5%', '5%'],
      axisLine: {
        lineStyle: {
          color: theme.strokeMid,
          width: 1,
        },
      },
      axisLabel: {
        color: theme.textHigh,
        fontSize: typography.axisLabelSize,
        rotate: isMobile ? 30 : 0,
        hideOverlap: true,
        formatter: (value) => (isMobile ? value.split(' - ')[0] : value),
      },
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      splitNumber: isMobile ? 3 : 4,
      axisLine: {
        lineStyle: {
          color: theme.strokeMid,
          width: 1,
        },
      },
      splitLine: {
        lineStyle: {
          color: theme.strokeLow,
          type: 'dashed',
          width: 1,
        },
      },
      axisLabel: {
        color: theme.textHigh,
        formatter: (value) => {
          return value >= 1000 ? `${value / 1000}k` : value;
        },
        fontSize: typography.axisLabelSize,
        hideOverlap: true,
      },
    },
    color: [colors.human, colors.bot],
    series: [
      {
        name: humanLabel,
        type: 'bar',
        data: humanData,
        barWidth: isMobile ? 12 : '30%',
        barMaxWidth: isMobile ? 14 : 24,
        itemStyle: {
          borderRadius: [3, 3, 0, 0],
        },
      },
      {
        name: botLabel,
        type: 'bar',
        data: botData,
        barWidth: isMobile ? 12 : '30%',
        barMaxWidth: isMobile ? 14 : 24,
        itemStyle: {
          borderRadius: [3, 3, 0, 0],
        },
      },
    ]
  }
}

const BarGraph = ({specs, setReady, showLegend = true}) => {
  let { isDarkMode } = useTheme();
  const { isMobile } = useMobile();
  const chartRef = useRef(null);
  const theme = useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);
  const chartOption = useMemo(
    () => getOption(specs, theme, isMobile, showLegend),
    [theme, specs, isMobile, showLegend]
  );

  useChartAutoResize(chartRef, [isDarkMode, isMobile, specs, showLegend]);

  const handleChartReady = (chart) => {
    chart.off('finished');
    chart.on('finished', () => {
      setReady('traffic');
    });
  };

  return (
    <ReactECharts ref={chartRef}
                  option={chartOption}
                  onChartReady={handleChartReady}
                  opts={{ renderer: isMobile ? 'svg' : 'canvas' }}
                  notMerge={true}
                  key={`${isDarkMode ? 'dark' : 'light'}-${isMobile ? 'm' : 'd'}`}
                  className="chart site-overview-chart-canvas"
                  style={{ width: '100%', height: '100%' }} />
  );
}

export default BarGraph;
